/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'

export type UiIconButtonVariant = 'filled' | 'light' | 'ghost'
export type UiIconButtonColor =
    | 'default'
    | 'primary'
    | 'danger'
    | 'success'
    | 'info'

interface UiIconButtonProps {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    title?: string
    disabled?: boolean
    size?: 'sm' | 'md' | 'lg'
    variant?: UiIconButtonVariant
    color?: UiIconButtonColor
    children?: React.ReactNode
    className?: string
}

const variantClasses: Record<
    UiIconButtonVariant,
    Record<UiIconButtonColor, string>
> = {
    ghost: {
        danger: 'bg-transparent text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20',
        primary:
            'bg-transparent text-cta-800 hover:bg-cta-50 dark:text-cta-400 dark:hover:bg-cta-900/20',
        success:
            'bg-transparent text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20',
        info: 'bg-transparent text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20',
        default:
            'bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800',
    },
    light: {
        danger: 'bg-white text-red-600 border border-red-200 hover:bg-red-50 dark:bg-gray-900 dark:text-red-400 dark:border-red-900/50 dark:hover:bg-red-900/20',
        primary:
            'bg-white text-cta-800 border border-cta-200 hover:bg-cta-50 dark:bg-gray-900 dark:text-cta-400 dark:border-cta-900/50 dark:hover:bg-cta-900/20',
        success:
            'bg-white text-green-600 border border-green-200 hover:bg-green-50 dark:bg-gray-900 dark:text-green-400 dark:border-green-900/50 dark:hover:bg-green-900/20',
        info: 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 dark:bg-gray-900 dark:text-blue-400 dark:border-blue-900/50 dark:hover:bg-blue-900/20',
        default:
            'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800',
    },
    filled: {
        danger: 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600',
        primary:
            'bg-cta-800 text-white hover:bg-cta-900 dark:bg-cta-700 dark:hover:bg-cta-600',
        success:
            'bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600',
        info: 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600',
        default:
            'bg-gray-800 text-white hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600',
    },
}

const UiIconButton = (props: UiIconButtonProps) => {
    const isSm = props.size === 'sm'
    const isLg = props.size === 'lg'
    const base =
        'inline-flex items-center justify-center rounded-md transition-all duration-200 focus:outline-none focus:border-primary-600 border border-transparent'
    const dim = isSm ? 'h-8 w-8' : isLg ? 'h-11 w-11' : 'h-9 w-9'

    const variant = props.variant || 'filled'
    const color = props.color || 'default'
    const variantClass = variantClasses[variant][color]

    return (
        <button
            className={clsx(
                'UiIconButton cursor-pointer',
                base,
                dim,
                variantClass,
                props.disabled && 'opacity-50 !cursor-not-allowed',
                props.className
            )}
            disabled={props.disabled}
            onClick={props.onClick && props.onClick}
            title={props.title}
            aria-label={props.title}
            type="button"
        >
            {props.children}
        </button>
    )
}

export default UiIconButton
